import { useCallback, useEffect } from "react";
import type { MutableRefObject } from "react";
import type { ApiConnection, ApiBounds } from "../../shared/interfaces";
import type * as UIExtension from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/UIExtension.full.js";
import { analyticsEvent, AnalyticsEvents } from "../../util/analytics";

export type OffsetInfo = {
    index: number;
    left: number;
    top: number;
    scale: number;
};

type PdfHistoryOpts = {
    /** Ref returned by the Foxit PDFUI instance */
    pdfUiRef: MutableRefObject<UIExtension.PDFUI | null>;
    /** Stable file-id so we invalidate history when the user opens another file */
    fileId: string | number;
    /** True when Foxit has finished first render */
    fileLoaded: boolean;
    /**
     * Delegate that performs the actual jump/scroll/zoom to the
     * destination bounds (your existing makeBoundsVisible logic)
     */
    jumpToBounds: (bounds: ApiBounds) => Promise<void>;
};

export function useJumpToConnectionWithHistory({
    pdfUiRef,
    fileId,
    fileLoaded,
    jumpToBounds,
}: PdfHistoryOpts) {
    /* cheap one-level compare avoids duplicate pushes */
    const offsetsEqual = (a?: OffsetInfo, b?: OffsetInfo) =>
        !!a &&
        !!b &&
        a.index === b.index &&
        a.left === b.left &&
        a.top === b.top &&
        a.scale === b.scale;

    /* merge with any keys React-Router has placed in history.state */
    const writePdfState = useCallback(
        (data: OffsetInfo, method: "push" | "replace" = "push") => {
            const base = window.history.state ?? {};
            const newState = { ...base, pdfViewerStep: { ...data, fileId } };
            method === "push"
                ? window.history.pushState(newState, "")
                : window.history.replaceState(newState, "");
        },
        [fileId],
    );

    /* -------------------------------------------------------------- */
    /* 1️⃣ – initialise root entry once the PDF is rendered            */
    /* -------------------------------------------------------------- */
    useEffect(() => {
        if (!fileLoaded || !pdfUiRef.current) return;

        (async () => {
            const pdfDocRender = await pdfUiRef.current!.getPDFDocRender();
            const init = (await pdfDocRender?.getOffsetInfo?.()) as OffsetInfo | undefined;
            if (init) writePdfState(init, "replace");
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileLoaded, fileId]);

    /* -------------------------------------------------------------- */
    /* 2️⃣ – popstate handler                                          */
    /* -------------------------------------------------------------- */
    useEffect(() => {
        const handler = async (e: PopStateEvent) => {
            const step = (e.state as { pdfViewerStep?: OffsetInfo })?.pdfViewerStep;
            if (!step || !pdfUiRef.current) return;

            await pdfUiRef.current.zoomTo(step.scale, {
                pageIndex: step.index,
                x: step.left,
                y: step.top,
            });
        };
        window.addEventListener("popstate", handler);
        return () => window.removeEventListener("popstate", handler);
    }, [fileId, pdfUiRef]);

    /* -------------------------------------------------------------- */
    /* 3️⃣ – handler to attach to each connection click                */
    /* -------------------------------------------------------------- */
    const handleConnectionClick = useCallback(
        async (connection: ApiConnection) => {
            if (!pdfUiRef.current) return;

            analyticsEvent(AnalyticsEvents.CONNECTION_CLICK);

            const pdfDocRender = await pdfUiRef.current.getPDFDocRender();
            const source = (await pdfDocRender?.getOffsetInfo?.()) as OffsetInfo | undefined;
            const current = (window.history.state as any)?.pdfViewerStep as OffsetInfo | undefined;

            /* save current position in-place */
            if (source && !offsetsEqual(current, source)) writePdfState(source, "replace");

            /* jump to destination */
            await jumpToBounds(connection.dstBounds);

            /* derive dest offset */
            const destOffset: OffsetInfo = {
                index: connection.dstBounds.page,
                left: 0,
                top: 0,
                scale: source?.scale ?? 1,
            };
            writePdfState(destOffset, "push");
        },
        [jumpToBounds, pdfUiRef, writePdfState],
    );

    return { handleConnectionClick };
}
